import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Send, Lock } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Release", "Royalty", "Payment", "Technical", "Account", "Other"];
const STATUSES = ["OPEN", "IN PROGRESS", "WAITING FOR USER", "RESOLVED", "CLOSED"];
const STAFF = ["super_admin", "operations", "accounts", "label_manager"];

export default function Tickets() {
  const { user } = useAuth();
  const isStaff = STAFF.includes(user.role);
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "Release", description: "" });
  const [attachment, setAttachment] = useState(null);

  const loadList = () => api.get("/tickets").then((r) => setList(r.data));
  const openTicket = (id) => api.get(`/tickets/${id}`).then((r) => { setActive(r.data); setReply(""); setInternal(false); });
  useEffect(() => { loadList(); }, []);

  const createTicket = async () => {
    if (!form.subject || !form.description) return toast.error("Subject and description required");
    const fd = new FormData();
    fd.append("subject", form.subject);
    fd.append("category", form.category);
    fd.append("description", form.description);
    if (attachment) fd.append("file", attachment);
    await api.post("/tickets", fd);
    toast.success("Ticket created");
    setCreateOpen(false);
    setForm({ subject: "", category: "Release", description: "" });
    setAttachment(null);
    loadList();
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    await api.post(`/tickets/${active.id}/messages`, { message: reply, is_internal: internal });
    setReply("");
    openTicket(active.id);
  };

  const changeStatus = async (status) => {
    await api.post(`/tickets/${active.id}/status`, { status });
    toast.success(`Status: ${status}`);
    openTicket(active.id);
    loadList();
  };

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Support Tickets</h1>
          <p className="mt-1 text-muted-foreground">{isStaff ? "Manage and respond to tickets." : "Raise and track support requests."}</p>
        </div>
        {!isStaff && <Button onClick={() => setCreateOpen(true)} data-testid="new-ticket-btn"><Plus className="mr-2 h-4 w-4" />New Ticket</Button>}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="divide-y divide-border lg:col-span-1" data-testid="ticket-list">
          {list.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No tickets.</p>}
          {list.map((t) => (
            <button key={t.id} onClick={() => openTicket(t.id)} className={`block w-full p-4 text-left hover:bg-accent ${active?.id === t.id ? "bg-accent" : ""}`} data-testid={`ticket-item-${t.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-semibold">{t.subject}</p>
                <StatusBadge status={t.status} />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{t.category} · {t.created_by_name}</p>
            </button>
          ))}
        </Card>

        <Card className="lg:col-span-2" data-testid="ticket-thread">
          {!active ? (
            <div className="flex h-full min-h-[300px] items-center justify-center text-muted-foreground">Select a ticket to view the conversation.</div>
          ) : (
            <div className="flex h-full flex-col">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold">{active.subject}</h3>
                  <p className="text-xs text-muted-foreground">{active.category} · by {active.created_by_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={active.status} />
                  {isStaff && (
                    <Select value={active.status} onValueChange={changeStatus}>
                      <SelectTrigger className="w-44" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto p-4">
                <div className="rounded-lg bg-accent p-3">
                  <p className="text-xs font-semibold">{active.created_by_name}</p>
                  <p className="text-sm">{active.description}</p>
                </div>
                {active.messages?.map((m) => (
                  <div key={m.id} className={`rounded-lg p-3 ${m.is_internal ? "border border-amber-500/30 bg-amber-500/5" : "bg-accent"}`} data-testid={`ticket-msg-${m.id}`}>
                    <p className="flex items-center gap-2 text-xs font-semibold">
                      {m.sender_name} <span className="text-muted-foreground">({m.sender_role})</span>
                      {m.is_internal && <span className="flex items-center gap-1 text-amber-400"><Lock className="h-3 w-3" />Internal</span>}
                    </p>
                    <p className="text-sm">{m.message}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-2 border-t border-border p-4">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" data-testid="ticket-reply-input" />
                <div className="flex items-center justify-between">
                  {isStaff ? (
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Checkbox checked={internal} onCheckedChange={setInternal} data-testid="internal-checkbox" />Internal note (hidden from user)
                    </label>
                  ) : <span />}
                  <Button onClick={sendReply} data-testid="send-reply-btn"><Send className="mr-2 h-4 w-4" />Send</Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Support Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" className="mt-1" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="ticket-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ticket-description" className="mt-1" /></div>
            <div><Label>Attachment (optional)</Label><Input type="file" onChange={(e) => setAttachment(e.target.files[0])} data-testid="ticket-attachment" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createTicket} data-testid="create-ticket-btn">Create Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
